import type { Node } from "reactflow"

import type { NodeType } from "../types/interfaces"

/**
 * Node metadata configuration for different node types.
 * Centralizes node-specific configuration and reduces duplication.
 */
export interface NodeMetadata {
  title: string
  iconClassName: string
  width?: number
  showTargetHandle?: boolean
  showSourceHandle?: boolean
  sourceHandleCount?: number
}

/**
 * Node metadata registry for all node types.
 * Makes it easy to add new node types without modifying existing code.
 */
export const NODE_METADATA: Record<NodeType, NodeMetadata> = {
  StartNode: {
    title: "Start",
    iconClassName: "card-type",
    width: 300,
    showTargetHandle: false,
    showSourceHandle: true,
    sourceHandleCount: 1,
  },
  RulesetNode: {
    title: "Ruleset",
    iconClassName: "RulesetCardType",
    width: 300,
    showTargetHandle: true,
    showSourceHandle: true,
    sourceHandleCount: 1,
  },
  DecisionNode: {
    title: "Decision",
    iconClassName: "RulesetCardType",
    width: 300,
    showTargetHandle: true,
    showSourceHandle: true,
    sourceHandleCount: 1,
  },
  ScriptNode: {
    title: "Script",
    iconClassName: "ScriptCardType",
    width: 300,
    showTargetHandle: true,
    showSourceHandle: true,
    sourceHandleCount: 1,
  },
  CalculatorNode: {
    title: "Calculator",
    iconClassName: "calculatorCardType",
    width: 300,
    showTargetHandle: true,
    showSourceHandle: true,
    sourceHandleCount: 1,
  },
  ProjectNode: {
    title: "Project",
    iconClassName: "ProjectCardType",
    width: 300,
    showTargetHandle: true,
    showSourceHandle: true,
    sourceHandleCount: 1,
  },
  ProgramNode: {
    title: "Program",
    iconClassName: "ProgramCardType",
    width: 300,
    showTargetHandle: true,
    showSourceHandle: true,
    sourceHandleCount: 1,
  },
  ScorecardsetNode: {
    title: "Scorecardset",
    iconClassName: "ScorecardsetCardType",
    width: 300,
    showTargetHandle: true,
    showSourceHandle: true,
    sourceHandleCount: 1,
  },
  TaglistNode: {
    title: "Taglist",
    iconClassName: "TaglistCardType",
    width: 300,
    showTargetHandle: true,
    showSourceHandle: true,
    sourceHandleCount: 1,
  },
  MarketPlaceScriptNode: {
    title: "Marketplace Script",
    iconClassName: "MarketplaceScriptCardType",
    width: 300,
    showTargetHandle: true,
    showSourceHandle: true,
    sourceHandleCount: 1,
  },
  LoopEndNode: {
    title: "Loop End",
    iconClassName: "loopEndCardType",
    width: 300,
    showTargetHandle: false,
    showSourceHandle: true,
    sourceHandleCount: 1,
  },
  LoopStartNode: {
    title: "Loop Start",
    iconClassName: "loopStartCardType",
    width: 300,
    showTargetHandle: false,
    showSourceHandle: true,
    sourceHandleCount: 1,
  },
  FilterNode: {
    title: "Filter",
    iconClassName: "filterCardType",
    width: 700,
    showTargetHandle: true,
    showSourceHandle: true,
    sourceHandleCount: 1,
  },
}

/**
 * Utility functions for node operations.
 * Provides common functionality used across all node types.
 */
export const nodeHelpers = {
  /**
   * Get metadata for a specific node type.
   */
  getNodeMetadata: (nodeType: NodeType): NodeMetadata => {
    return NODE_METADATA[nodeType] || NODE_METADATA.StartNode
  },

  /**
   * Validate node state and return validation result.
   */
  validateNodeState: (node: Node): { isValid: boolean; errors: string[] } => {
    const errors: string[] = []

    if (!node.id) {
      errors.push("Node ID is required")
    }

    if (!node.type) {
      errors.push("Node type is required")
    }

    if (!node.data) {
      errors.push("Node data is required")
    }

    return {
      isValid: errors.length === 0,
      errors,
    }
  },

  /**
   * Check if a node is in a specific state.
   */
  isNodeInState: (node: Node, state: string): boolean => {
    return node.data?.state === state
  },

  /**
   * Get node display name for UI.
   */
  getNodeDisplayName: (node: Node): string => {
    return node.data?.name || node.data?.title || node.type || "Unknown Node"
  },

  /**
   * Check if node has required data for operation.
   */
  hasRequiredData: (node: Node, requiredFields: string[]): boolean => {
    return requiredFields.every((field) => node.data?.[field] !== undefined)
  },
}

/**
 * Node validation rules for different node types.
 * Centralizes validation logic and makes it reusable.
 */
export const NODE_VALIDATION_RULES: Record<NodeType, string[]> = {
  StartNode: [],
  RulesetNode: ["ruleset"],
  DecisionNode: ["ruleset"],
  ScriptNode: ["script"],
  CalculatorNode: ["calculator"],
  ProjectNode: ["project"],
  ProgramNode: ["program"],
  ScorecardsetNode: ["scorecardset"],
  TaglistNode: ["taglist"],
  MarketPlaceScriptNode: ["script"],
  LoopStartNode: ["loop_start"],
  LoopEndNode: ["loop_end"],
  FilterNode: ["filter"],
}

/**
 * Get validation rules for a specific node type.
 */
export function getNodeValidationRules(nodeType: NodeType): string[] {
  return NODE_VALIDATION_RULES[nodeType] || []
}
